import React from 'react';

const OptionsSpreads = ({ currentLesson, setCurrentLesson, currentState, updateTutorialState, currentTutorialInfo, userStats, setUserStats, setTutorialComplete, tutorialComplete }) => {
  
  // COMPLETE MODULE CONTENT - ALL 7 MODULES FULLY IMPLEMENTED
  const moduleContent = {
    1: {
      title: "Introduction to Spreads",
      icon: "📊",
      content: {
        keyPoints: [
          "Spreads combine multiple options to create specific risk/reward profiles",
          "Reduce premium cost compared to single options",
          "Limited risk and limited reward strategies",
          "Profit from direction, time decay, or volatility changes"
        ],
        example: "Instead of buying a $2,000 call, buy a $1,000 call and sell a $500 call for net cost of $500. Lower cost, capped profit."
      },
      quiz: [
        {
          id: 'q1',
          question: "What is the main advantage of spread strategies?",
          options: ["Higher profit potential", "Lower cost than single options", "Guaranteed profits", "No risk involved"],
          correct: 1,
          explanation: "Correct! Spreads typically cost less than single options because you're selling one option to help pay for another."
        },
        {
          id: 'q2',
          question: "Spread strategies have:",
          options: ["Unlimited risk and reward", "Limited risk and limited reward", "No risk, unlimited reward", "High risk, no reward"],
          correct: 1,
          explanation: "Correct! Most spread strategies have both limited risk and limited reward, providing defined risk/reward profiles."
        }
      ]
    },
    2: {
      title: "Vertical Spreads - Bull Call",
      icon: "📈",
      content: {
        keyPoints: [
          "Buy lower strike call, sell higher strike call",
          "Bullish strategy - profit when underlying rises",
          "Maximum profit = difference in strikes minus net premium paid",
          "Maximum loss = net premium paid"
        ],
        example: "BTC at $50k. Buy $50k call for $3k, sell $55k call for $1k. Net cost $2k. Max profit $3k (at $55k+), max loss $2k."
      },
      quiz: [
        {
          id: 'q1',
          question: "In a bull call spread, you:",
          options: ["Buy high strike, sell low strike", "Buy low strike, sell high strike", "Buy two calls", "Sell two calls"],
          correct: 1,
          explanation: "Correct! Bull call spread: BUY the lower strike call and SELL the higher strike call."
        },
        {
          id: 'q2',
          question: "When is max profit achieved in a bull call spread?",
          options: ["At the lower strike", "At the higher strike or above", "Between the strikes", "Below both strikes"],
          correct: 1,
          explanation: "Correct! Maximum profit is achieved when the underlying is at or above the higher strike price at expiration."
        },
        {
          id: 'q3',
          question: "What's the breakeven point for a bull call spread?",
          options: ["Lower strike + net premium paid", "Higher strike - net premium paid", "Lower strike - net premium paid", "Higher strike + net premium paid"],
          correct: 0,
          explanation: "Correct! Breakeven = Lower strike price + Net premium paid. You need the underlying above this level to profit."
        }
      ]
    },
    3: {
      title: "Vertical Spreads - Bear Put",
      icon: "📉",
      content: {
        keyPoints: [
          "Buy higher strike put, sell lower strike put",
          "Bearish strategy - profit when underlying falls",
          "Maximum profit = difference in strikes minus net premium paid",
          "Maximum loss = net premium paid"
        ],
        example: "BTC at $50k. Buy $50k put for $3k, sell $45k put for $1k. Net cost $2k. Max profit $3k (at $45k or below), max loss $2k."
      },
      quiz: [
        {
          id: 'q1',
          question: "In a bear put spread, you:",
          options: ["Buy low strike put, sell high strike put", "Buy high strike put, sell low strike put", "Buy two puts", "Sell two puts"],
          correct: 1,
          explanation: "Correct! Bear put spread: BUY the higher strike put and SELL the lower strike put."
        },
        {
          id: 'q2',
          question: "Bear put spreads profit when the underlying:",
          options: ["Rises significantly", "Falls significantly", "Stays the same", "Becomes more volatile"],
          correct: 1,
          explanation: "Correct! Bear put spreads are bearish strategies that profit when the underlying price decreases."
        },
        {
          id: 'q3',
          question: "What's the breakeven point for a bear put spread?",
          options: ["Higher strike + net premium paid", "Higher strike - net premium paid", "Lower strike + net premium paid", "Lower strike - net premium paid"],
          correct: 1,
          explanation: "Correct! Breakeven = Higher strike price - Net premium paid. You need the underlying below this level to profit."
        }
      ]
    },
    4: {
      title: "Iron Condors",
      icon: "🦅",
      content: {
        keyPoints: [
          "Combine bull put spread + bear call spread",
          "Profit from low volatility and sideways movement",
          "Sell options closer to current price, buy options further away",
          "Maximum profit if underlying stays between short strikes"
        ],
        example: "BTC at $50k. Sell $48k put & $52k call, buy $45k put & $55k call. Profit if BTC stays $48k-$52k range."
      },
      quiz: [
        {
          id: 'q1',
          question: "Iron condors profit when:",
          options: ["Price moves significantly up", "Price moves significantly down", "Price stays within a range", "Volatility increases dramatically"],
          correct: 2,
          explanation: "Correct! Iron condors profit when the underlying stays within the range of the short strikes (low volatility)."
        },
        {
          id: 'q2',
          question: "An iron condor consists of:",
          options: ["Four options of the same type", "Two calls and two puts", "Only calls", "Only puts"],
          correct: 1,
          explanation: "Correct! Iron condors use two calls and two puts - it's a combination of a bull put spread and bear call spread."
        },
        {
          id: 'q3',
          question: "Maximum profit in an iron condor is achieved when:",
          options: ["Above the call strikes", "Below the put strikes", "Between the short strikes", "At any strike price"],
          correct: 2,
          explanation: "Correct! Max profit occurs when the underlying closes between the two short strikes at expiration."
        }
      ]
    },
    5: {
      title: "Butterfly Spreads",
      icon: "🦋",
      content: {
        keyPoints: [
          "Buy 1 low strike + 1 high strike, sell 2 middle strikes",
          "Profit when underlying stays near middle strike",
          "Limited risk and limited reward",
          "Can use all calls or all puts"
        ],
        example: "BTC at $50k. Buy $48k call, sell 2x $50k calls, buy $52k call. Max profit if BTC exactly at $50k at expiration."
      },
      quiz: [
        {
          id: 'q1',
          question: "Butterfly spreads work best when:",
          options: ["High volatility expected", "Low volatility expected", "Direction is certain", "Time to expiration is long"],
          correct: 1,
          explanation: "Correct! Butterflies profit from low volatility when the underlying stays near the middle strike."
        },
        {
          id: 'q2',
          question: "In a butterfly spread, you:",
          options: ["Buy 1, sell 2, buy 1", "Buy 2, sell 1, buy 1", "Sell 1, buy 2, sell 1", "Buy 3 options"],
          correct: 0,
          explanation: "Correct! Butterfly: BUY 1 low strike, SELL 2 middle strikes, BUY 1 high strike."
        },
        {
          id: 'q3',
          question: "Maximum profit in a butterfly occurs when:",
          options: ["At the wing strikes", "At the body (middle) strike", "Anywhere between strikes", "Outside the strikes"],
          correct: 1,
          explanation: "Correct! Maximum profit occurs when the underlying closes exactly at the middle (body) strike at expiration."
        }
      ]
    },
    6: {
      title: "Calendar Spreads",
      icon: "📅",
      content: {
        keyPoints: [
          "Same strikes, different expiration dates",
          "Sell short-term option, buy long-term option",
          "Profit from time decay of short-term option",
          "Works best when underlying stays near strike price"
        ],
        example: "BTC at $50k. Sell 30-day $50k call for $2k, buy 60-day $50k call for $3k. Net cost $1k. Profit as 30-day decays faster."
      },
      quiz: [
        {
          id: 'q1',
          question: "Calendar spreads use:",
          options: ["Same expiration, different strikes", "Same strikes, different expirations", "Different strikes and expirations", "Only monthly options"],
          correct: 1,
          explanation: "Correct! Calendar spreads use the SAME strike prices but DIFFERENT expiration dates."
        },
        {
          id: 'q2',
          question: "Calendar spreads profit primarily from:",
          options: ["Large price movements", "Time decay differences", "Volatility increases", "Interest rate changes"],
          correct: 1,
          explanation: "Correct! Calendar spreads profit from time decay, as the short-term option decays faster than the long-term option."
        },
        {
          id: 'q3',
          question: "In a calendar spread, you typically:",
          options: ["Sell long-term, buy short-term", "Sell short-term, buy long-term", "Buy both options", "Sell both options"],
          correct: 1,
          explanation: "Correct! Calendar spreads involve SELLING the short-term option and BUYING the long-term option."
        }
      ]
    },
    7: {
      title: "Advanced Spread Combinations",
      icon: "🎯",
      content: {
        keyPoints: [
          "Combine multiple spread strategies for complex payoffs",
          "Ratio spreads: unequal number of long/short options",
          "Diagonal spreads: different strikes AND expirations",
          "Adjust spreads as market conditions change"
        ],
        example: "Double diagonal: Calendar spread + different strikes. Sell 30-day $48k & $52k, buy 60-day $45k & $55k for range trading."
      },
      quiz: [
        {
          id: 'q1',
          question: "Ratio spreads involve:",
          options: ["Equal numbers of long and short options", "Unequal numbers of long and short options", "Only long options", "Only short options"],
          correct: 1,
          explanation: "Correct! Ratio spreads use unequal numbers of long and short positions (e.g., buy 1, sell 2)."
        },
        {
          id: 'q2',
          question: "Diagonal spreads combine:",
          options: ["Same strikes, same expirations", "Different strikes, same expirations", "Same strikes, different expirations", "Different strikes and different expirations"],
          correct: 3,
          explanation: "Correct! Diagonal spreads use DIFFERENT strike prices AND DIFFERENT expiration dates."
        },
        {
          id: 'q3',
          question: "The key to successful spread trading is:",
          options: ["Always holding to expiration", "Never adjusting positions", "Understanding risk/reward profiles", "Using maximum leverage"],
          correct: 2,
          explanation: "Correct! Success requires understanding each spread's risk/reward profile and when to use each strategy."
        }
      ]
    }
  };

  const currentModuleContent = moduleContent[currentLesson];
  if (!currentModuleContent) return null;

  if (currentState.phase === 'intro') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="text-6xl mb-4">{currentModuleContent.icon}</div>
          <h3 className="text-white text-2xl font-bold mb-2">Module {currentLesson}: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Advanced spread strategies for options trading</p>
        </div>

        <div className="bg-atticus-navy-800 rounded-lg p-6 space-y-4">
          <div className="border-l-4 border-yellow-500 pl-4">
            <h4 className="text-white font-bold mb-2">🎯 Key Concepts</h4>
            <div className="space-y-2">
              {currentModuleContent.content.keyPoints.map((point, index) => (
                <p key={index} className="text-gray-300 text-sm">• {point}</p>
              ))}
            </div>
          </div>

          <div className="bg-purple-500/20 border border-purple-500/50 rounded-lg p-4">
            <h5 className="text-purple-400 font-bold mb-2">📊 Trading Example</h5>
            <p className="text-white text-sm">{currentModuleContent.content.example}</p>
          </div>
        </div>

        <button
          onClick={() => updateTutorialState({ phase: 'quiz' })}
          className="w-full bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-bold transition-colors"
        >
          Test Spread Knowledge →
        </button>
      </div>
    );
  }

  if (currentState.phase === 'quiz') {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h3 className="text-white text-2xl font-bold mb-2">Knowledge Check: {currentModuleContent.title}</h3>
          <p className="text-gray-400">Master these spread concepts</p>
        </div>

        {currentModuleContent.quiz.map((question, qIndex) => {
          const userAnswer = currentState.quizAnswers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correct;

          return (
            <div key={question.id} className="bg-atticus-navy-800 rounded-lg p-6">
              <div className="mb-4">
                <h4 className="text-white font-bold mb-3">Question {qIndex + 1}: {question.question}</h4>
                <div className="space-y-2">
                  {question.options.map((option, oIndex) => (
                    <button
                      key={oIndex}
                      onClick={() => updateTutorialState({ 
                        quizAnswers: { ...currentState.quizAnswers, [question.id]: oIndex }
                      })}
                      disabled={isAnswered}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isAnswered
                          ? oIndex === question.correct 
                            ? 'bg-green-500/20 border-green-500 text-green-300'
                            : oIndex === userAnswer
                              ? 'bg-red-500/20 border-red-500 text-red-300'
                              : 'bg-gray-700 border-gray-600 text-gray-400'
                          : 'bg-atticus-navy-700 border-gray-600 text-gray-300 hover:border-yellow-500 hover:text-white'
                      }`}
                    >
                      {option}
                      {isAnswered && oIndex === question.correct && <span className="float-right text-green-400">✓</span>}
                      {isAnswered && oIndex === userAnswer && oIndex !== question.correct && <span className="float-right text-red-400">✗</span>}
                    </button>
                  ))}
                </div>
              </div>

              {isAnswered && (
                <div className={`p-3 rounded-lg border ${isCorrect ? 'bg-green-500/20 border-green-500' : 'bg-red-500/20 border-red-500'}`}>
                  <p className={`text-sm ${isCorrect ? 'text-green-300' : 'text-red-300'}`}>
                    {isCorrect ? '✅ Correct! ' : '❌ Incorrect. '}{question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}

        {Object.keys(currentState.quizAnswers).length === currentModuleContent.quiz.length && (
          <div className="text-center">
            {currentModuleContent.quiz.every(q => currentState.quizAnswers[q.id] === q.correct) ? (
              <div className="space-y-4">
                <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
                  <div className="text-green-400 font-bold text-lg">🎉 Module {currentLesson} Complete!</div>
                  <div className="text-green-300">You've mastered {currentModuleContent.title}!</div>
                </div>
                <button
                  onClick={() => {
                    const moduleXP = currentTutorialInfo.xp_per_module;
                    setUserStats(prev => ({
                      ...prev,
                      completed_lessons: [...prev.completed_lessons, `options_spreads_module_${currentLesson}`],
                      total_xp: prev.total_xp + moduleXP,
                      badges: currentLesson === 1 ? [...prev.badges, 'Spread Trader'] : prev.badges
                    }));

                    if (currentLesson < currentTutorialInfo.modules) {
                      setCurrentLesson(currentLesson + 1);
                      updateTutorialState({ phase: 'intro', quizAnswers: {} });
                    } else {
                      setTutorialComplete(true);
                      setUserStats(prev => ({
                        ...prev,
                        completed_tutorials: [...prev.completed_tutorials, 'options_spreads'],
                        certification_progress: Math.min(prev.certification_progress + 15, 100),
                        badges: [...prev.badges, 'Spread Master']
                      }));
                    }
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-8 py-3 rounded-lg font-bold transition-colors"
                >
                  {currentLesson < currentTutorialInfo.modules ? `Continue to Module ${currentLesson + 1} →` : 'Complete Tutorial →'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-orange-500/20 border border-orange-500 rounded-lg p-4">
                  <div className="text-orange-400 font-bold">📖 Review Required</div>
                  <div className="text-orange-300">Please review the material and try again.</div>
                </div>
                <button
                  onClick={() => updateTutorialState({ quizAnswers: {}, phase: 'intro' })}
                  className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-lg font-bold transition-colors"
                >
                  Review Module {currentLesson}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default OptionsSpreads;
